/**
 * Overlay URL Loader
 *
 * Utilities for resolving overlay URLs when loading a project.
 * Handles the case where saved overlays may have stale blob URLs
 * that need to be resolved using stored metadata (storageId, fileHash, fileName).
 *
 * Used by useProjectStateFromUrl to fix URLs on project load.
 */

import { ConvexReactClient } from 'convex/react';
import { Overlay, ClipOverlay, SoundOverlay, ImageOverlay } from '../../types';
import { getUserMediaItems, UserMediaItem } from './indexdb';
import { getUserId } from './user-id';
import { isBlobUrl, isMediaOverlay, isConvexUrl } from './overlay-url-resolver';
import { ConvexMediaService } from '../../services/convex-media-service';

/**
 * Resolution method used for logging/debugging
 */
export type ResolutionMethod = 'storageId' | 'fileHash' | 'fileName' | 'localMedia' | 'none';

/**
 * Result of resolving a single overlay
 */
export interface OverlayResolution {
  overlayId: number;
  resolved: boolean;
  method: ResolutionMethod;
  oldUrl: string;
  newUrl: string;
  error?: string;
}

/**
 * Result of resolving all overlays on load
 */
export interface LoadResolutionResult {
  overlays: Overlay[];
  resolutions: OverlayResolution[];
  resolvedCount: number;
  failedCount: number;
  totalMediaOverlays: number;
}

/**
 * Resolve URL by file name from local media items
 *
 * @param fileName - Original file name
 * @param localMediaItems - Array of local media items from IndexedDB
 * @returns Matching media item or null if not found
 */
export function resolveByFileName(
  fileName: string,
  localMediaItems: UserMediaItem[]
): UserMediaItem | null {
  return localMediaItems.find((item) => item.name === fileName) || null;
}

/**
 * Resolve a single media overlay's URL on load using pre-fetched URL maps
 *
 * Resolution strategy (in order):
 * 1. If URL is valid HTTPS, keep as-is
 * 2. Try resolve by mediaStorageId (from pre-fetched map)
 * 3. Try resolve by mediaFileHash (from pre-fetched map)
 * 4. Try resolve by mediaFileName in local media
 * 5. If all fail, mark as missing
 */
async function resolveMediaOverlayOnLoad(
  overlay: ClipOverlay | SoundOverlay | ImageOverlay,
  storageIdUrlMap: Map<string, string | null>,
  fileHashUrlMap: Map<string, string | null>,
  localMediaItems: UserMediaItem[]
): Promise<{ overlay: ClipOverlay | SoundOverlay | ImageOverlay; resolution: OverlayResolution }> {
  const oldUrl = overlay.src;

  // If URL is valid HTTPS, no resolution needed
  if (isConvexUrl(oldUrl)) {
    return {
      overlay,
      resolution: {
        overlayId: overlay.id,
        resolved: true,
        method: 'none',
        oldUrl,
        newUrl: oldUrl,
      },
    };
  }

  // URL needs resolution (blob URL or invalid)
  console.log(`[OverlayUrlLoader] Resolving overlay ${overlay.id} with blob URL`);

  // Strategy 1: Try by storage ID (from pre-fetched map)
  if (overlay.mediaStorageId) {
    const url = storageIdUrlMap.get(overlay.mediaStorageId) ?? null;
    if (url) {
      console.log(`[OverlayUrlLoader] Resolved overlay ${overlay.id} by storageId`);
      return {
        overlay: { ...overlay, src: url },
        resolution: {
          overlayId: overlay.id,
          resolved: true,
          method: 'storageId',
          oldUrl,
          newUrl: url,
        },
      };
    }
  }

  // Strategy 2: Try by file hash (from pre-fetched map)
  if (overlay.mediaFileHash) {
    const url = fileHashUrlMap.get(overlay.mediaFileHash) ?? null;
    if (url) {
      console.log(`[OverlayUrlLoader] Resolved overlay ${overlay.id} by fileHash`);
      return {
        overlay: { ...overlay, src: url },
        resolution: {
          overlayId: overlay.id,
          resolved: true,
          method: 'fileHash',
          oldUrl,
          newUrl: url,
        },
      };
    }
  }

  // Strategy 3: Try by file name in local media
  if (overlay.mediaFileName) {
    const matchingMedia = resolveByFileName(overlay.mediaFileName, localMediaItems);
    if (matchingMedia) {
      // Prefer Convex URL over blob URL
      const resolvedUrl = matchingMedia.convexUrl || matchingMedia.serverPath;
      if (resolvedUrl && !isBlobUrl(resolvedUrl)) {
        console.log(`[OverlayUrlLoader] Resolved overlay ${overlay.id} by fileName`);
        return {
          overlay: {
            ...overlay,
            src: resolvedUrl,
            mediaStorageId: matchingMedia.convexStorageId || overlay.mediaStorageId,
            mediaFileHash: matchingMedia.fileHash || overlay.mediaFileHash,
          },
          resolution: {
            overlayId: overlay.id,
            resolved: true,
            method: 'fileName',
            oldUrl,
            newUrl: resolvedUrl,
          },
        };
      }
    }
  }

  // Strategy 4: Try matching any local media by URL (fallback for old projects)
  const matchingByUrl = localMediaItems.find(
    (item) => item.serverPath === oldUrl || item.convexUrl === oldUrl
  );
  if (matchingByUrl && matchingByUrl.convexUrl) {
    console.log(`[OverlayUrlLoader] Resolved overlay ${overlay.id} by localMedia URL match`);
    return {
      overlay: {
        ...overlay,
        src: matchingByUrl.convexUrl,
        mediaStorageId: matchingByUrl.convexStorageId,
        mediaFileHash: matchingByUrl.fileHash,
        mediaFileName: matchingByUrl.name,
      },
      resolution: {
        overlayId: overlay.id,
        resolved: true,
        method: 'localMedia',
        oldUrl,
        newUrl: matchingByUrl.convexUrl,
      },
    };
  }

  // All strategies failed
  console.warn(`[OverlayUrlLoader] Failed to resolve overlay ${overlay.id}`);
  return {
    overlay,
    resolution: {
      overlayId: overlay.id,
      resolved: false,
      method: 'none',
      oldUrl,
      newUrl: oldUrl,
      error: 'No matching media found',
    },
  };
}

/**
 * Resolve all overlay URLs when loading a project
 *
 * This function:
 * 1. Pre-fetches all needed URLs in 2 batch queries
 * 2. Resolves all overlays in parallel using Promise.allSettled
 * 3. Falls back to local media matching if needed
 *
 * @param overlays - Array of overlays from saved project
 * @param convexClient - Authenticated Convex client (or null if not available)
 * @returns Updated overlays with resolved URLs and resolution report
 */
export async function resolveOverlaysOnLoad(
  overlays: Overlay[],
  convexClient: ConvexReactClient | null
): Promise<LoadResolutionResult> {
  // Get local media items for fallback matching
  const userId = getUserId();
  const localMediaItems = await getUserMediaItems(userId);

  // Pre-fetch: collect all storageIds and fileHashes that need resolution
  const storageIds: string[] = [];
  const fileHashes: string[] = [];

  for (const overlay of overlays) {
    if (!isMediaOverlay(overlay)) continue;
    if (isConvexUrl(overlay.src)) continue;

    if (overlay.mediaStorageId) {
      storageIds.push(overlay.mediaStorageId);
    }
    if (overlay.mediaFileHash) {
      fileHashes.push(overlay.mediaFileHash);
    }
  }

  // Batch fetch URLs in parallel (2 queries instead of N)
  let storageIdUrlMap = new Map<string, string | null>();
  let fileHashUrlMap = new Map<string, string | null>();

  if (convexClient && (storageIds.length > 0 || fileHashes.length > 0)) {
    const [storageResult, hashResult] = await Promise.all([
      storageIds.length > 0
        ? ConvexMediaService.getFileUrlsBatch(convexClient, storageIds)
        : Promise.resolve(new Map<string, string | null>()),
      fileHashes.length > 0
        ? ConvexMediaService.getUrlsByHashBatch(convexClient, fileHashes)
        : Promise.resolve(new Map<string, string | null>()),
    ]);
    storageIdUrlMap = storageResult;
    fileHashUrlMap = hashResult;
  }

  // Resolve all overlays in parallel
  const promises = overlays.map(async (overlay) => {
    if (!isMediaOverlay(overlay)) {
      return {
        overlay,
        resolution: null,
      };
    }

    // Skip overlays that already have valid URLs
    if (isConvexUrl(overlay.src)) {
      return {
        overlay,
        resolution: {
          overlayId: overlay.id,
          resolved: true,
          method: 'none' as ResolutionMethod,
          oldUrl: overlay.src,
          newUrl: overlay.src,
        },
      };
    }

    const result = await resolveMediaOverlayOnLoad(
      overlay,
      storageIdUrlMap,
      fileHashUrlMap,
      localMediaItems
    );
    return {
      overlay: result.overlay,
      resolution: result.resolution,
    };
  });

  const settled = await Promise.allSettled(promises);

  const resolvedOverlays: Overlay[] = [];
  const resolutions: OverlayResolution[] = [];
  let resolvedCount = 0;
  let failedCount = 0;
  let totalMediaOverlays = 0;

  for (const result of settled) {
    if (result.status === 'fulfilled') {
      resolvedOverlays.push(result.value.overlay);
      if (result.value.resolution) {
        totalMediaOverlays++;
        resolutions.push(result.value.resolution);
        if (result.value.resolution.resolved && result.value.resolution.method !== 'none') {
          resolvedCount++;
        } else if (!result.value.resolution.resolved) {
          failedCount++;
        }
      }
    } else {
      // Promise rejected — should not happen since resolveMediaOverlayOnLoad handles errors
      console.error('[OverlayUrlLoader] Unexpected rejection:', result.reason);
      failedCount++;
    }
  }

  // Log resolution report
  if (resolvedCount > 0 || failedCount > 0) {
    console.log(
      `[OverlayUrlLoader] Load resolution complete:`,
      `\n  Resolved: ${resolvedCount}`,
      `\n  Failed: ${failedCount}`,
      `\n  Total media overlays: ${totalMediaOverlays}`
    );

    // Log details of resolutions
    const methodCounts: Record<ResolutionMethod, number> = {
      storageId: 0,
      fileHash: 0,
      fileName: 0,
      localMedia: 0,
      none: 0,
    };
    resolutions.forEach((r) => {
      if (r.resolved) methodCounts[r.method]++;
    });
    console.log(`[OverlayUrlLoader] Resolution methods:`, methodCounts);

    // Log failed resolutions
    const failed = resolutions.filter((r) => !r.resolved);
    if (failed.length > 0) {
      console.warn(
        `[OverlayUrlLoader] Failed to resolve ${failed.length} overlay(s):`,
        failed.map((r) => ({
          overlayId: r.overlayId,
          oldUrl: r.oldUrl.substring(0, 50) + '...',
          error: r.error,
        }))
      );
    }
  }

  return {
    overlays: resolvedOverlays,
    resolutions,
    resolvedCount,
    failedCount,
    totalMediaOverlays,
  };
}

/**
 * Check if a project has overlays that need URL resolution
 *
 * @param overlays - Array of overlays to check
 * @returns True if any overlay has a blob URL or invalid URL
 */
export function needsUrlResolution(overlays: Overlay[]): boolean {
  return overlays.some((overlay) => {
    if (!isMediaOverlay(overlay)) return false;
    return isBlobUrl(overlay.src) || !isConvexUrl(overlay.src);
  });
}
